// Club-authored terms (entry fee terms, meet terms) reduced to a formatting
// allow-list before they reach v-html.
//
// WHY. A club admin types these and members read them before paying. Rendered
// as-is, a club admin could put a <script> or an onerror= in front of every
// member who opens the pay dialog (stored XSS, UK GDPR Art 32). The API now
// sanitises them on save and on read; this is the second lock, so a response
// from an older API, a cached payload or a path we missed is still safe here.
//
// HOW. A port of the API's EmailTermsFormat.Sanitise (storage mode), case for
// case, so the two agree on every input: change them together. No DOM, so it
// runs the same during server rendering. Kept: p, br, strong, b, em, i, u, ul,
// ol, li, blockquote, h1-h6 and a. Script, style, frames, forms and the like go
// with everything inside them; any other tag goes and its text stays. Every
// attribute goes except a link's href, and only an http, https or mailto one.
// Text between tags keeps its entities; a stray < or > becomes one, so nothing
// the browser reads as a tag can hide in the text. Unclosed tags are closed.

const LOOKS_LIKE_HTML = /<[a-z][^>]*>/i;
const TOKEN = /<!--[\s\S]*?-->|<(\/?)([a-zA-Z][a-zA-Z0-9]*)((?:[^>"']|"[^"]*"|'[^']*')*)>/g;
const HREF = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;

const KEPT = new Set(['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'blockquote', 'a',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6']);
const DROPPED_WITH_CONTENT = new Set(['script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template',
  'svg', 'math', 'head', 'title', 'textarea', 'select']);

const NAMED = {amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', colon: ':', tab: '\t', newline: '\n'};

function decodeEntities(s) {
  return s.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);?/gi, (match, body) => {
    if (body[0] === '#') {
      const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
    }
    const named = NAMED[body.toLowerCase()];
    return named ?? match;
  });
}

const encodeAttribute = (s) => s
    .replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
    .replace(/</g, '&lt;').replace(/>/g, '&gt;');

const encodeAngleBrackets = (s) => s.replace(/</g, '&lt;').replace(/>/g, '&gt;');

function safeHref(attributes) {
  const m = HREF.exec(attributes);
  if (!m) return null;
  const href = decodeEntities(m[1] ?? m[2] ?? m[3] ?? '').trim();
  return /^(https?:\/\/|mailto:)/i.test(href) ? href : null;
}

export function looksLikeHtml(terms) {
  return typeof terms === 'string' && LOOKS_LIKE_HTML.test(terms);
}

/** Returns plain text untouched (callers escape it); HTML reduced to the allow-list. */
export function sanitiseTermsHtml(terms) {
  if (!terms || !looksLikeHtml(terms)) return terms ?? '';

  let output = '';
  const open = [];
  let position = 0;
  let skippingUntil = null;
  const text = (s) => { if (skippingUntil == null) output += encodeAngleBrackets(s); };

  for (const m of terms.matchAll(TOKEN)) {
    if (m.index > position) text(terms.slice(position, m.index));
    position = m.index + m[0].length;
    if (m[0].startsWith('<!--')) continue;

    const closing = m[1] === '/';
    const tag = m[2].toLowerCase();
    if (skippingUntil != null) {
      if (closing && tag === skippingUntil) skippingUntil = null;
      continue;
    }
    if (DROPPED_WITH_CONTENT.has(tag)) {
      // Never closed: everything after it goes too, which is the safe direction.
      if (!closing) skippingUntil = tag;
      continue;
    }
    if (!KEPT.has(tag)) continue;

    if (closing) {
      const at = open.lastIndexOf(tag);
      if (at < 0) continue;
      for (let i = open.length - 1; i >= at; i--) output += `</${open[i]}>`;
      open.length = at;
      continue;
    }
    if (tag === 'br') {
      output += '<br>';
      continue;
    }
    if (tag === 'a') {
      const href = safeHref(m[3]);
      if (href == null) continue;
      output += `<a href="${encodeAttribute(href)}" target="_blank" rel="noopener noreferrer">`;
    } else {
      output += `<${tag}>`;
    }
    open.push(tag);
  }
  if (position < terms.length) text(terms.slice(position));
  for (let i = open.length - 1; i >= 0; i--) output += `</${open[i]}>`;
  return output;
}
